import {
  EditorTools,
  EditorUtils,
  getHtml,
  ProseMirror,
} from '@progress/kendo-react-editor';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../common/widgets/Button';
import Editor from '../../../../../../common/widgets/Editor';
import Icons from '../../../../../../common/widgets/Icons';
import Input from '../../../../../../common/widgets/Input';
import TextArea from '../../../../../../common/widgets/TextArea';
import { ErrorState } from '../../../../../benefit/models/Errors';
import { resources } from '../../../../resources/resources';
import {
  addressField,
  brandDescriptionField,
  descriptionField,
  emailField,
  howToOrderField,
  nameField,
  phoneNumberField,
  websiteField,
} from '../constants';
import {
  getEnField,
  getEnName,
  ProductDetailsForm,
  setEditorFontOnMount,
} from '../utils';

interface TranslationsTabProps {
  detailsForm: ProductDetailsForm;
  setDetailsForm: (form: ProductDetailsForm) => void;
  errors: ErrorState;
  setErrors: (errors: ErrorState) => void;
  isSaveAsDraftButtonLoading?: boolean;
  isSendForReviewButtonLoading?: boolean;
  buttonFunctions?: {
    onBackClick: () => void;
    onSaveClick: (sendForReview: boolean) => void;
  };
  showContactDetailsFields: boolean;
}

const { Bold, Italic, Underline, Strikethrough, UnorderedList, OrderedList } =
  EditorTools;

export const Translations = (props: TranslationsTabProps) => {
  const {
    detailsForm,
    setDetailsForm,
    isSaveAsDraftButtonLoading,
    isSendForReviewButtonLoading,
    errors,
    setErrors,
    buttonFunctions,
    showContactDetailsFields,
  } = props;
  const { t: translate } = useTranslation();
  const [descriptionEditorValue, setDescriptionEditorValue] = useState(
    EditorUtils.createDocument(
      new ProseMirror.Schema({
        nodes: EditorUtils.nodes,
        marks: EditorUtils.marks,
      }),
      detailsForm.productDescriptions.en.description?.value ?? '',
    ),
  );

  useEffect(() => {
    setDetailsForm(
      produce(detailsForm, d => {
        d.productDescriptions.en.address = getEnField(
          d.productDescriptions.ro.address?.value,
          addressField,
        );
        d.productDescriptions.en.phoneNumber = getEnField(
          d.productDescriptions.ro.phoneNumber?.value,
          phoneNumberField,
        );
        d.productDescriptions.en.email = getEnField(
          d.productDescriptions.ro.email?.value,
          emailField,
        );
        d.productDescriptions.en.website = getEnField(
          d.productDescriptions.ro.website?.value,
          websiteField,
        );
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearErrors = (field: string) => {
    setErrors(
      produce(errors, d => {
        d[field] = [];
      }),
    );
  };

  return (
    <>
      <div className="m-panel translations-panel">
        <h1>{translate(resources.products.add.translations.title)}</h1>

        <div className="translation-languages">
          <div>
            <span>
              {translate(resources.products.add.translations.romanian)}
            </span>
            <Icons.MFlagRo />
          </div>
          <div>
            <span>
              {translate(resources.products.add.translations.english)}
            </span>
            <Icons.MFlagUk />
          </div>
        </div>

        {/* Product name */}

        <div className="translation-section">
          <div className="translation-label">
            {translate(resources.products.add.productDetails.name.label)}
            {'*'}
          </div>
          <div className="translation-readonly">
            <Input
              value={detailsForm.productDescriptions.ro.name?.value}
              mandatory
              readOnly
            />
          </div>
          <div>
            <Input
              value={detailsForm.productDescriptions.en.name?.value ?? ''}
              onChange={e =>
                setDetailsForm(
                  produce(detailsForm, d => {
                    d.productDescriptions.en.name = getEnField(
                      e.value,
                      nameField,
                    );
                  }),
                )
              }
              errors={errors[getEnName(nameField)]}
              clearErrors={() => clearErrors(getEnName(nameField))}
            />
          </div>
        </div>

        {/* Brand description */}

        {showContactDetailsFields && (
          <div className="translation-section">
            <div className="translation-label">
              {translate(
                resources.products.add.productDetails.contact.brandDescription,
              )}
              {'*'}
            </div>
            <div className="translation-readonly">
              <span>
                {detailsForm.productDescriptions.ro.brandDescription?.value}
              </span>
            </div>
            <div>
              <TextArea
                value={
                  detailsForm.productDescriptions.en.brandDescription?.value ??
                  ''
                }
                onChange={e =>
                  setDetailsForm(
                    produce(detailsForm, d => {
                      d.productDescriptions.en.brandDescription = getEnField(
                        e.value,
                        brandDescriptionField,
                      );
                    }),
                  )
                }
                errors={errors[getEnName(brandDescriptionField)]}
                clearErrors={() =>
                  clearErrors(getEnName(brandDescriptionField))
                }
                rows={6}
              />
            </div>
          </div>
        )}

        {/* Product description */}

        <div className="translation-section">
          <div className="translation-label">
            {translate(resources.products.add.productDetails.description.label)}
            {'*'}
          </div>
          <div className="translation-readonly">
            <span
              dangerouslySetInnerHTML={{
                __html:
                  detailsForm.productDescriptions.ro.description?.value ?? '',
              }}
            />
          </div>
          <div>
            <Editor
              tools={[
                Bold,
                Italic,
                Underline,
                Strikethrough,
                UnorderedList,
                OrderedList,
              ]}
              value={descriptionEditorValue}
              onChange={e => {
                setDescriptionEditorValue(e.value as any);
                setDetailsForm(
                  produce(detailsForm, d => {
                    d.productDescriptions.en.description = getEnField(
                      getHtml({
                        doc: e.value as any,
                      }),
                      descriptionField,
                    );
                  }),
                );
              }}
              errors={errors[getEnName(descriptionField)]}
              clearErrors={() => clearErrors(getEnName(descriptionField))}
              onMount={setEditorFontOnMount}
            />
          </div>
        </div>

        {showContactDetailsFields && (
          <>
            <h2>
              {translate(resources.products.add.productDetails.contact.title)}
            </h2>

            {/* Address */}

            <div className="translation-section">
              <div className="translation-label">
                {translate(
                  resources.products.add.productDetails.contact.address,
                )}
              </div>
              <div className="translation-readonly">
                <Input
                  value={detailsForm.productDescriptions.ro.address?.value}
                  readOnly
                />
              </div>
              <div>
                <Input
                  value={
                    detailsForm.productDescriptions.en.address?.value ?? ''
                  }
                  onChange={e =>
                    setDetailsForm(
                      produce(detailsForm, d => {
                        d.productDescriptions.en.address = getEnField(
                          e.value,
                          addressField,
                        );
                      }),
                    )
                  }
                  errors={errors[getEnName(addressField)]}
                  clearErrors={() => clearErrors(getEnName(addressField))}
                />
              </div>
            </div>

            {/* Mail */}

            <div className="translation-section">
              <div className="translation-label">
                {translate(resources.products.add.productDetails.contact.email)}
              </div>
              <div className="translation-readonly">
                <Input
                  value={detailsForm.productDescriptions.ro.email?.value}
                  readOnly
                />
              </div>
              <div>
                <Input
                  value={detailsForm.productDescriptions.en.email?.value ?? ''}
                  onChange={e =>
                    setDetailsForm(
                      produce(detailsForm, d => {
                        d.productDescriptions.en.email = getEnField(
                          e.value,
                          emailField,
                        );
                      }),
                    )
                  }
                  errors={errors[getEnName(emailField)]}
                  clearErrors={() => clearErrors(getEnName(emailField))}
                />
              </div>
            </div>

            {/* Phone */}

            <div className="translation-section">
              <div className="translation-label">
                {translate(
                  resources.products.add.productDetails.contact.phoneNumber,
                )}
              </div>
              <div className="translation-readonly">
                <Input
                  value={detailsForm.productDescriptions.ro.phoneNumber?.value}
                  readOnly
                />
              </div>
              <div>
                <Input
                  value={
                    detailsForm.productDescriptions.en.phoneNumber?.value ?? ''
                  }
                  onChange={e =>
                    setDetailsForm(
                      produce(detailsForm, d => {
                        d.productDescriptions.en.phoneNumber = getEnField(
                          e.value,
                          phoneNumberField,
                        );
                      }),
                    )
                  }
                  errors={errors[getEnName(phoneNumberField)]}
                  clearErrors={() => clearErrors(getEnName(phoneNumberField))}
                />
              </div>
            </div>

            {/* Website */}

            <div className="translation-section">
              <div className="translation-label">
                {translate(
                  resources.products.add.productDetails.contact.website,
                )}
              </div>
              <div className="translation-readonly">
                <Input
                  value={detailsForm.productDescriptions.ro.website?.value}
                  readOnly
                />
              </div>
              <div>
                <Input
                  value={
                    detailsForm.productDescriptions.en.website?.value ?? ''
                  }
                  onChange={e =>
                    setDetailsForm(
                      produce(detailsForm, d => {
                        d.productDescriptions.en.website = getEnField(
                          e.value,
                          websiteField,
                        );
                      }),
                    )
                  }
                  errors={errors[getEnName(websiteField)]}
                  clearErrors={() => clearErrors(getEnName(websiteField))}
                />
              </div>
            </div>
          </>
        )}

        {/* How to order */}
        <h2>
          {translate(resources.products.add.productDetails.howToOrder.label)}
        </h2>
        <div className="translation-section">
          <div className="translation-readonly">
            <span>{detailsForm.productDescriptions.ro.howToOrder?.value}</span>
          </div>
          <div>
            <TextArea
              value={detailsForm.productDescriptions.en.howToOrder?.value ?? ''}
              onChange={e =>
                setDetailsForm(
                  produce(detailsForm, d => {
                    d.productDescriptions.en.howToOrder = getEnField(
                      e.value,
                      howToOrderField,
                    );
                  }),
                )
              }
              errors={errors[getEnName(howToOrderField)]}
              clearErrors={() => clearErrors(getEnName(howToOrderField))}
              rows={6}
            />
          </div>
        </div>
      </div>

      {buttonFunctions && (
        <div className="m-actions-panel translations-actions">
          <Button
            onClick={buttonFunctions.onBackClick}
            className="btn btn-outline"
            disabled={
              isSaveAsDraftButtonLoading || isSendForReviewButtonLoading
            }
          >
            <span className="btn-text">{translate(resources.back)}</span>
          </Button>
          <Button
            onClick={() => buttonFunctions.onSaveClick(false)}
            className="btn btn-outline"
            loading={isSaveAsDraftButtonLoading}
            disabled={isSendForReviewButtonLoading}
          >
            <span className="btn-text">
              {translate(resources.products.add.saveAsDraft)}
            </span>
          </Button>
          <Button
            onClick={() => buttonFunctions.onSaveClick(true)}
            className="btn btn-solid"
            loading={isSendForReviewButtonLoading}
            disabled={isSaveAsDraftButtonLoading}
          >
            <span className="btn-text">
              {translate(resources.products.add.sendForReview)}
            </span>
          </Button>
        </div>
      )}
    </>
  );
};
